import { cn } from '@/lib/utils'
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx'
import { AnchorHTMLAttributes, ComponentProps } from 'react'

const SecureLink = ({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
	const normalizedHref = href?.trim()
	const isRootRelative = (() => {
		if (!normalizedHref || !normalizedHref.startsWith('/') || normalizedHref.startsWith('//')) {
			return false
		}

		try {
			return new URL(normalizedHref, window.location.origin).origin === window.location.origin
		} catch {
			return false
		}
	})()
	const isHashLink = normalizedHref?.startsWith('#')
	const isHttpUrl = (() => {
		if (!normalizedHref) { return false }
		try {
			const url = new URL(normalizedHref)
			return url.protocol === 'http:' || url.protocol === 'https:'
		} catch {
			return false
		}
	})()
	const isSafe = Boolean(normalizedHref && (isHttpUrl || isRootRelative || isHashLink))

	if (!isSafe) {
		// If URL is not safe, render as plain text
		return <span>{children}</span>
	}

	return (
		<a
			{...props}
			href={normalizedHref}
			target={isHttpUrl ? '_blank' : props.target}
			rel={isHttpUrl ? 'noopener noreferrer' : props.rel}
		>
			{children}
		</a>
	)
}

/**
 * Secure markdown options that prevent XSS attacks:
 * - Disables raw HTML rendering
 * - Sanitizes links to prevent javascript: and data: URLs
 * - Adds rel="noopener noreferrer" to all external links
 */
export const secureMarkdownOptions: MarkdownToJSX.Options = {
	// Disable parsing of raw HTML
	disableParsingRawHTML: true,

	// Override default elements with secure versions
	overrides: {
		a: {
			component: SecureLink,
		},
		// Explicitly disable potentially dangerous elements
		script: {
			component: () => null,
		},
		iframe: {
			component: () => null,
		},
		object: {
			component: () => null,
		},
		embed: {
			component: () => null,
		},
	},
}

/**
 * Props for SecureMarkdown component with children typed as string
 */
export interface SecureMarkdownProps extends Omit<ComponentProps<typeof Markdown>, 'children'> {
	children: string
}

/**
 * Secure markdown component for rendering AI-generated content
 *
 * Security features:
 * - Raw HTML is completely disabled
 * - Links are validated and sanitized
 * - External links open in new tabs with security attributes
 * - Dangerous elements (script, iframe, etc.) are blocked
 */
export const SecureMarkdown = ({
	className,
	children,
	options,
	...props
}: SecureMarkdownProps) => (
	<Markdown
		className={cn(
			'prose prose-sm dark:prose-invert max-w-none',
			className,
		)}
		options={{
			...options,
			...secureMarkdownOptions,
			overrides: {
				...options?.overrides,
				...secureMarkdownOptions.overrides,
			},
		}}
		{...props}
	>
		{children}
	</Markdown>
)
